import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.get("/auth/me")
            .then((res) => mounted && setUser(res.data))
            .catch(() => mounted && setUser(false))
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message };
        }
    };

    const requestOtp = async (phone, purpose = "customer") => {
        try {
            const { data } = await api.post("/auth/otp/request", { phone, purpose });
            return { ok: true, ...data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message };
        }
    };

    const verifyOtp = async (phone, code) => {
        try {
            const { data } = await api.post("/auth/otp/verify", { phone, code });
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            // Server-side logout best-effort: client always clears local state below.
            // Logging helps diagnose token/cookie issues without alarming the user.
            console.warn("logout: server call failed, clearing local session anyway:", err?.message || err);
        }
        setUser(false);
    };

    const value = useMemo(() => ({ user, loading, login, logout, requestOtp, verifyOtp }), [user, loading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
